import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useUser } from '../contexts/UserContext'
import { companyService } from '../services/companyService'
import { userService } from '../services/userService'
import type { BillingContact, Company, User } from '../types/user'
import Button from './Button'
import CompanyLogoUpload from './CompanyLogoUpload'
import GooglePlacesSelect, {
  type AddressComponents,
} from './GooglePlacesSelect'

const CompanySettingsTab: React.FC = () => {
  const { user, refreshUser } = useUser()
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    yard_address: '',
    yard_city: '',
    yard_state: '',
    yard_zip_code: '',
  })
  const [addressValue, setAddressValue] = useState<{
    label: string
    value: string
  } | null>(null)
  const [yardAddressValue, setYardAddressValue] = useState<{
    label: string
    value: string
  } | null>(null)
  const [companyId, setCompanyId] = useState<number | null>(null)
  const [billingContact, setBillingContact] = useState<BillingContact | null>(
    null,
  )
  const [teamMembers, setTeamMembers] = useState<User[]>([])
  const [selectedBillingContactId, setSelectedBillingContactId] = useState<
    number | null
  >(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false)
  const [isUpdatingBilling, setIsUpdatingBilling] = useState(false)
  const [logoRemoved, setLogoRemoved] = useState(false)

  // Load company data, billing contact, and team members
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        // Load company data
        const company = await userService.getUserCompany()
        if (company) {
          setCompanyId(company.id)
          setCompanyData({
            name: company.name || '',
            address: company.address || '',
            street_address: company.street_address || '',
            city: company.city || '',
            state: company.state || '',
            zip_code: company.zip_code || '',
            yard_address: company.yard_address || '',
            yard_city: company.yard_city || '',
            yard_state: company.yard_state || '',
            yard_zip_code: company.yard_zip_code || '',
          })
          // Set address value for GooglePlacesSelect
          if (company.address) {
            setAddressValue({
              label: company.address,
              value: company.address,
              addressComponents: company.street_address
                ? {
                    street_address: company.street_address,
                    city: company.city || '',
                    state: company.state || '',
                    zip_code: company.zip_code || '',
                  }
                : undefined,
            })
          }
          // Set yard address value for GooglePlacesSelect
          if (company.yard_address) {
            setYardAddressValue({
              label: company.yard_address,
              value: company.yard_address,
              addressComponents: company.yard_city
                ? {
                    street_address: company.yard_address,
                    city: company.yard_city || '',
                    state: company.yard_state || '',
                    zip_code: company.yard_zip_code || '',
                  }
                : undefined,
            })
          }
        }

        // Load current billing contact
        const billingContactResponse = await companyService.getBillingContact()
        setBillingContact(billingContactResponse.billing_contact)
        setSelectedBillingContactId(
          billingContactResponse.billing_contact?.id || null,
        )

        // Load team members for billing contact dropdown
        const members = await companyService.getTeamMembers()
        setTeamMembers(members)
      } catch (error) {
        console.error('Failed to load company data:', error)
        toast.error('Failed to load company data')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [])

  const handleCompanyInputChange = (field: string, value: string) => {
    setCompanyData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddressChange = (
    option: {
      label: string
      value: string
      addressComponents?: AddressComponents
    } | null,
  ) => {
    setAddressValue(option)
    setCompanyData((prev) => ({
      ...prev,
      address: option?.value || '',
      street_address: option?.addressComponents?.street_address || '',
      city: option?.addressComponents?.city || '',
      state: option?.addressComponents?.state || '',
      zip_code: option?.addressComponents?.zip_code || '',
    }))
  }

  const handleYardAddressChange = (
    option: {
      label: string
      value: string
      addressComponents?: AddressComponents
    } | null,
  ) => {
    setYardAddressValue(option)
    setCompanyData((prev) => ({
      ...prev,
      yard_address: option?.value || '',
      yard_city: option?.addressComponents?.city || '',
      yard_state: option?.addressComponents?.state || '',
      yard_zip_code: option?.addressComponents?.zip_code || '',
    }))
  }

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!companyId) {
      toast.error('No company found')
      return
    }

    setIsUpdatingCompany(true)
    try {
      await userService.updateCompany(companyId, {
        name: companyData.name,
        address: companyData.address,
        street_address: companyData.street_address,
        city: companyData.city,
        state: companyData.state,
        zip_code: companyData.zip_code,
        yard_address: companyData.yard_address,
        yard_city: companyData.yard_city,
        yard_state: companyData.yard_state,
        yard_zip_code: companyData.yard_zip_code,
      })
      toast.success('Company information updated successfully')
      await refreshUser()
    } catch (error) {
      console.error('Failed to update company:', error)
      toast.error('Failed to update company information')
    } finally {
      setIsUpdatingCompany(false)
    }
  }

  const handleUpdateBillingContact = async () => {
    if (!selectedBillingContactId) {
      toast.error('Please select a billing contact')
      return
    }

    setIsUpdatingBilling(true)
    try {
      const response = await companyService.setBillingContact({
        user_id: selectedBillingContactId,
      })
      setBillingContact(response.billing_contact)
      toast.success('Billing contact updated successfully')
    } catch (error) {
      console.error('Failed to update billing contact:', error)
      toast.error('Failed to update billing contact')
    } finally {
      setIsUpdatingBilling(false)
    }
  }

  if (isLoading) {
    return (
      <div className={css(styles.loadingContainer)}>
        <div>
          <i className="fa-solid fa-spinner fa-spin" /> Loading company
          settings...
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      {/* Company Information Section */}
      <div className={css(styles.section)}>
        <div className={css(styles.sectionHeader)}>
          <h3 className={css(styles.sectionTitle)}>Company Information</h3>
          <p className={css(styles.sectionSubtitle)}>
            Update your company's basic information
          </p>
        </div>

        <form onSubmit={handleUpdateCompany} className={css(styles.form)}>
          <div className={css(styles.formGroup)}>
            <label htmlFor="companyName" className={css(styles.label)}>
              Company Name
            </label>
            <input
              id="companyName"
              type="text"
              value={companyData.name}
              onChange={(e) => handleCompanyInputChange('name', e.target.value)}
              className={css(styles.input)}
              placeholder="Enter company name"
            />
          </div>

          <div className={css(styles.formGroup)}>
            <label htmlFor="companyAddress" className={css(styles.label)}>
              Company Address
            </label>
            <GooglePlacesSelect
              value={addressValue}
              onChange={handleAddressChange}
              placeholder="Enter company address"
              className={styles.input}
            />
          </div>

          {/* Parsed Address Fields (Read-only) */}
          {(companyData.city || companyData.state || companyData.zip_code) && (
            <div className={css(styles.parsedAddressContainer)}>
              <div className={css(styles.parsedAddressRow)}>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>City</label>
                  <input
                    type="text"
                    value={companyData.city}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>
                    State
                  </label>
                  <input
                    type="text"
                    value={companyData.state}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={companyData.zip_code}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={css(styles.formGroup)}>
            <label htmlFor="yardAddress" className={css(styles.label)}>
              Yard Address
            </label>
            <GooglePlacesSelect
              value={yardAddressValue}
              onChange={handleYardAddressChange}
              placeholder="Enter yard address"
              className={styles.input}
            />
          </div>

          {/* Parsed Yard Address Fields (Read-only) */}
          {(companyData.yard_city ||
            companyData.yard_state ||
            companyData.yard_zip_code) && (
            <div className={css(styles.parsedAddressContainer)}>
              <div className={css(styles.parsedAddressRow)}>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>City</label>
                  <input
                    type="text"
                    value={companyData.yard_city}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>
                    State
                  </label>
                  <input
                    type="text"
                    value={companyData.yard_state}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
                <div className={css(styles.parsedAddressField)}>
                  <label className={css(styles.parsedAddressLabel)}>
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={companyData.yard_zip_code}
                    readOnly
                    className={css(styles.parsedAddressInput)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={css(styles.formActions)}>
            <Button type="submit" disabled={isUpdatingCompany}>
              {isUpdatingCompany ? 'Updating...' : 'Update Company'}
            </Button>
          </div>
        </form>
      </div>

      {/* Company Logo Section */}
      <div className={css(styles.section)}>
        <div className={css(styles.sectionHeader)}>
          <h3 className={css(styles.sectionTitle)}>Company Logo</h3>
          <p className={css(styles.sectionSubtitle)}>
            Upload and manage your company logo
          </p>
        </div>

        <div className={css(styles.logoSection)}>
          <CompanyLogoUpload
            currentLogoUrl={logoRemoved ? null : user?.logo_url}
            onLogoUpdate={async (logoUrl) => {
              console.log('CompanySettingsTab logo update:', logoUrl)
              if (logoUrl === null) {
                // Logo was removed - set state to show empty state
                setLogoRemoved(true)
              } else {
                // Logo was uploaded - reset removed state
                setLogoRemoved(false)
                // Refresh user context to reflect changes
                await refreshUser()
              }
            }}
            size="large"
            showRemoveButton={true}
          />
        </div>
      </div>

      {/* Billing Contact Section */}
      <div className={css(styles.section)}>
        <div className={css(styles.sectionHeader)}>
          <h3 className={css(styles.sectionTitle)}>Billing Contact</h3>
          <p className={css(styles.sectionSubtitle)}>
            Designate who handles company billing and payments
          </p>
        </div>

        <div className={css(styles.billingContactContainer)}>
          {billingContact && (
            <div className={css(styles.currentBillingContact)}>
              <h4 className={css(styles.currentContactTitle)}>
                Current Billing Contact
              </h4>
              <div className={css(styles.contactInfo)}>
                <div className={css(styles.contactDetail)}>
                  <span className={css(styles.contactLabel)}>Name:</span>
                  <span>
                    {billingContact.first_name} {billingContact.last_name}
                  </span>
                </div>
                <div className={css(styles.contactDetail)}>
                  <span className={css(styles.contactLabel)}>Email:</span>
                  <span>{billingContact.email}</span>
                </div>
                <div className={css(styles.contactDetail)}>
                  <span className={css(styles.contactLabel)}>Role:</span>
                  <span>{billingContact.user_type}</span>
                </div>
              </div>
            </div>
          )}

          <div className={css(styles.billingContactForm)}>
            <div className={css(styles.formGroup)}>
              <label
                htmlFor="billingContactSelect"
                className={css(styles.label)}
              >
                {billingContact
                  ? 'Change Billing Contact'
                  : 'Select Billing Contact'}
              </label>
              <select
                id="billingContactSelect"
                value={selectedBillingContactId || ''}
                onChange={(e) =>
                  setSelectedBillingContactId(Number(e.target.value))
                }
                className={css(styles.select)}
              >
                <option value="">Select a team member</option>
                {teamMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.first_name} {member.last_name} ({member.email}) -{' '}
                    {member.user_type}
                  </option>
                ))}
              </select>
            </div>

            <Button
              onClick={handleUpdateBillingContact}
              disabled={isUpdatingBilling || !selectedBillingContactId}
            >
              {isUpdatingBilling ? 'Updating...' : 'Update Billing Contact'}
            </Button>
          </div>
        </div>
      </div>

      {/* Pricing Information Section */}
      <div className={css(styles.section)}>
        <div className={css(styles.sectionHeader)}>
          <h3 className={css(styles.sectionTitle)}>Pricing Information</h3>
          <p className={css(styles.sectionSubtitle)}>
            Company billing structure and usage fees
          </p>
        </div>

        <div className={css(styles.pricingContainer)}>
          <div className={css(styles.pricingItem)}>
            <span className={css(styles.pricingLabel)}>Tag Creation:</span>
            <span className={css(styles.pricingValue)}>$0.95 each</span>
          </div>
          <div className={css(styles.pricingItem)}>
            <span className={css(styles.pricingLabel)}>Dispatch Creation:</span>
            <span className={css(styles.pricingValue)}>$9.90 each</span>
          </div>
          <div className={css(styles.pricingItem)}>
            <span className={css(styles.pricingLabel)}>Project Creation:</span>
            <span className={css(styles.pricingValue)}>$19.90 each</span>
          </div>
          <div className={css(styles.pricingItem)}>
            <span className={css(styles.pricingLabel)}>Invoice Creation:</span>
            <span className={css(styles.pricingValue)}>$19.90 each</span>
          </div>
        </div>

        <div className={css(styles.billingNote)}>
          <p className={css(styles.noteText)}>
            <strong>Note:</strong> All usage fees are charged to the company
            account and handled by the designated billing contact.
          </p>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '32px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px 0',
  },
  section: {
    marginBottom: '40px',
    paddingBottom: '32px',
    borderBottom: '1px solid #E5E7EB',
    ':last-child': {
      borderBottom: 'none',
      marginBottom: '0',
      paddingBottom: '0',
    },
  },
  sectionHeader: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111928',
    margin: '0 0 4px 0',
    fontFamily: 'Inter, sans-serif',
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '8px',
    fontFamily: 'Inter, sans-serif',
  },
  input: {
    padding: '12px 16px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111928',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 3px rgba(28, 100, 242, 0.1)',
    },
  },
  textarea: {
    padding: '12px 16px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111928',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    resize: 'vertical',
    minHeight: '80px',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 3px rgba(28, 100, 242, 0.1)',
    },
  },
  select: {
    padding: '12px 16px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111928',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 3px rgba(28, 100, 242, 0.1)',
    },
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '8px',
  },
  billingContactContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  currentBillingContact: {
    backgroundColor: '#F0F9FF',
    border: '1px solid #BAE6FD',
    borderRadius: '8px',
    padding: '16px',
  },
  currentContactTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#0369A1',
    margin: '0 0 12px 0',
    fontFamily: 'Inter, sans-serif',
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  contactDetail: {
    display: 'flex',
    gap: '8px',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
  },
  contactLabel: {
    fontWeight: 500,
    color: '#374151',
    minWidth: '50px',
  },
  billingContactForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  pricingContainer: {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  pricingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
  },
  pricingLabel: {
    color: '#374151',
    fontWeight: 500,
  },
  pricingValue: {
    color: '#111928',
    fontWeight: 600,
  },
  billingNote: {
    backgroundColor: '#FEF3C7',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '16px',
  },
  noteText: {
    fontSize: '14px',
    color: '#92400E',
    margin: 0,
    fontFamily: 'Inter, sans-serif',
  },
  logoSection: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  parsedAddressContainer: {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '16px',
  },
  parsedAddressRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
  parsedAddressField: {
    display: 'flex',
    flexDirection: 'column',
  },
  parsedAddressLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6B7280',
    marginBottom: '4px',
    fontFamily: 'Inter, sans-serif',
  },
  parsedAddressInput: {
    padding: '8px 12px',
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#374151',
    fontFamily: 'Inter, sans-serif',
  },
})

export default CompanySettingsTab
